// src/features/layout/entityPanelRegistrations.tsx
// The one always-loaded module that wires feature panels into the rail's
// entity-panel registry. Imported by RightRail so registration runs no matter
// which route loaded first; the feature modules themselves never import the
// rail, so the graph stays rail → registry ← features.

import type { Entity } from '@core/types/entity';
import { EntityChatPanel } from '@/features/chat/EntityChatPanel';
import { EntityTaskPanel } from '@/features/task/EntityTaskPanel';
import { EntitySchedulePanel } from '@/features/calendar/EntitySchedulePanel';
import { TaskWorkRailPanel } from '@/features/task/TaskWorkRailPanel';
import { registerEntityPanel, type EntityPanelProps } from './entityPanels';

export function MessageCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

export function CheckSquareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 11 12 14 22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  );
}

export function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

export function WrenchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  );
}

// The task and calendar panels take the core Entity; the rail context carries
// only its identity, so the adapter hands over just that.
export function TaskPanelAdapter({ entity }: EntityPanelProps) {
  return <EntityTaskPanel entity={entity as Entity} />;
}

export function SchedulePanelAdapter({ entity }: EntityPanelProps) {
  return <EntitySchedulePanel entity={entity as Entity} />;
}

export function ChatPanelAdapter({ entity, title }: EntityPanelProps) {
  return <EntityChatPanel entity={entity} defaultConversationName={title} />;
}

export function WorkRailPanelAdapter({ entity }: EntityPanelProps) {
  return <TaskWorkRailPanel entity={entity} />;
}

registerEntityPanel({
  key: 'work',
  icon: WrenchIcon,
  labelKey: 'layout.rail.work',
  component: WorkRailPanelAdapter,
  appliesTo: ['task'],
});

registerEntityPanel({
  key: 'discussion',
  icon: MessageCircleIcon,
  labelKey: 'layout.rail.discussion',
  component: ChatPanelAdapter,
});

registerEntityPanel({
  key: 'tasks',
  icon: CheckSquareIcon,
  labelKey: 'layout.rail.tasks',
  component: TaskPanelAdapter,
  // Documents do not have a task aspect. Opening this panel for a document
  // asks TaskService for activity on an unsupported target type.
  excludesTypes: ['document', 'core.document'],
});

registerEntityPanel({
  key: 'schedule',
  icon: CalendarIcon,
  labelKey: 'layout.rail.schedule',
  component: SchedulePanelAdapter,
});
